import React, { useMemo, useState } from 'react';
import styles from './MegaMenu.module.css';
import Icons from '../../../../../../components/Icons/Icons';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../../../util/routes';
import { brandModels } from '../../../../../../config/configExtras';

const TOPS = ['Adult Boots', 'Custom Boot Services'];

const groundTypes = [
  { label: 'Firm Ground Grass', value: 'firm_ground' },
  { label: 'Soft Ground Grass', value: 'soft_ground' },
  { label: 'Artificial Grass', value: 'artificial_grass' },
  { label: 'Turf', value: 'turf' },
  { label: 'Futsal/Indoor', value: 'futsal_indoor' },
  { label: 'Anti-Clog', value: 'anti_clog' },
];

const conditions = [{ label: 'Brand New', value: 'sell-new-products' }, { label: 'Used', value: 'sell-used-products' }];

const normalize = s => (s || '').toLowerCase();

const MegaMenu = props => {
  const { viewport, onClose, isDrawer } = props;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;

  const [activeCategory, setActiveCategory] = useState(null);

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const navigate = params => {
    const searchPath = createResourceLocatorString('SearchPage', routeConfiguration, {}, params);
    history.push(searchPath);
    if (isDrawer && onClose) onClose();
  };

  // ROUTING HELPERS (tweak param keys if your schema differs)
  const goBrand = brand => navigate({ pub_brandname: `has_any:${normalize(brand)}` });

  const goBrandModel = (brand, model) =>
    navigate({
      pub_brandname: `has_any:${normalize(brand)}`,
      pub_model: `has_any:${normalize(model)}`,
    });

  const goCondition = val => navigate({ pub_listingType: val });

  const goGround = val => navigate({ pub_ground: `has_any:${val}` });

  const goKidsRoom = () => {
    // If you have a dedicated kids route, swap 'SearchPage' for that.
    // Adjust the filter key (pub_isKids) to your schema.
    navigate({ pub_isKids: true });
  };

  // DATA PREP
  const filteredBrands = useMemo(
    () => (brandModels || []).filter(b => normalize(b.brand) !== 'other'),
    []
  );

  const adidasModels = useMemo(() => {
    const ad = (brandModels || []).find(b => normalize(b.brand) === 'adidas');
    return ad?.models || [];
  }, []);

  const nikeModels = useMemo(() => {
    const nk = (brandModels || []).find(b => normalize(b.brand) === 'nike');
    return nk?.models || [];
  }, []);

  // UI ACTION
  const onTopItemActivate = item => {
    if (item === 'Junior Boot Room') {
      goKidsRoom();
      return;
    }
    if (isMobileLayout) {
      setActiveCategory(prev => (prev === item ? null : item));
    }
  };

  const showPanel = cat => (!isMobileLayout ? activeCategory === cat : activeCategory === cat);

  return (
    <div className={styles.menu}>
      {TOPS.map(top => (
        <div
          key={top}
          className={styles.menuItem}
          onMouseEnter={() =>
            !isMobileLayout && top !== 'Junior Boot Room' && setActiveCategory(top)
          }
          onMouseLeave={() =>
            !isMobileLayout && top !== 'Junior Boot Room' && setActiveCategory(null)
          }
        >
          <span
            role="button"
            tabIndex={0}
            className={styles.topLink}
            onClick={() => onTopItemActivate(top)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onTopItemActivate(top);
            }}
          >
            {top} {isMobileLayout && top !== 'Junior Boot Room' && <Icons name="nextIcon" />}
          </span>

          {/* ADULT BOOTS DROPDOWN */}
          {top === 'Adult Boots' && showPanel('Adult Boots') && (
            <div className={styles.megaMenu}>
              {/* Brands */}
              <div className={styles.brandContainer}>
                <div className={styles.groupTitle}>Brands</div>
                <div className={styles.modelsMenu}>
                  {filteredBrands.map(b => (
                    <div
                      key={`brand-${b.brand}`}
                      className={styles.subLink}
                      role="button"
                      tabIndex={0}
                      onClick={() => goBrand(b.brand)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && goBrand(b.brand)}
                    >
                      {b.brand}
                    </div>
                  ))}
                </div>
              </div>

              {/* Adidas Collections */}
              {adidasModels?.length > 0 && (
                <div className={styles.brandContainer}>
                  <div className={styles.groupTitle}>Adidas Collections</div>
                  <div className={styles.modelsMenu}>
                    {adidasModels.map(m => (
                      <div
                        key={`adidas-${m}`}
                        className={styles.subLink}
                        role="button"
                        tabIndex={0}
                        onClick={() => goBrandModel('adidas', m)}
                        onKeyDown={e =>
                          (e.key === 'Enter' || e.key === ' ') && goBrandModel('adidas', m)
                        }
                      >
                        {`adidas ${m}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nike Collections */}
              {nikeModels?.length > 0 && (
                <div className={styles.brandContainer}>
                  <div className={styles.groupTitle}>Nike Collections</div>
                  <div className={styles.modelsMenu}>
                    {nikeModels.map(m => (
                      <div
                        key={`nike-${m}`}
                        className={styles.subLink}
                        role="button"
                        tabIndex={0}
                        onClick={() => goBrandModel('Nike', m)}
                        onKeyDown={e =>
                          (e.key === 'Enter' || e.key === ' ') && goBrandModel('Nike', m)
                        }
                      >
                        {`Nike ${m}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Condition */}
              <div className={styles.brandContainer}>
                <div className={styles.groupTitle}>Condition</div>
                <div className={styles.modelsMenu}>
                  {conditions.map(c => (
                    <div
                      key={`cond-${c.value}`}
                      className={styles.subLink}
                      role="button"
                      tabIndex={0}
                      onClick={() => goCondition(c.value)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && goCondition(c.value)}
                    >
                      {c.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Ground Type */}
              <div className={styles.brandContainer}>
                <div className={styles.groupTitle}>Ground Type</div>
                <div className={styles.modelsMenu}>
                  {groundTypes.map(g => (
                    <div
                      key={`ground-${g.value}`}
                      className={styles.subLink}
                      role="button"
                      tabIndex={0}
                      onClick={() => goGround(g.value)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && goGround(g.value)}
                    >
                      {g.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CUSTOM BOOT SERVICES DROPDOWN */}
          {top === 'Custom Boot Services' && showPanel('Custom Boot Services') && (
            <div className={styles.megaMenu}>
              <div className={styles.brandContainer}>
                <div className={styles.groupTitle}>Custom boot services</div>
                <div className={styles.modelsMenu}>
                  {[
                    'Stud conversion service',
                    'Custom blackout service',
                    'Boot ID personalisation service',
                    'Sock conversion service',
                    'Tongue strap service',
                  ].map((svc, idx) => (
                    <div
                      key={`svc-${idx}`}
                      className={styles.subLink}
                      role="button"
                      tabIndex={0}
                      // If these are actual search facets later, replace with navigate({...})
                      onClick={() => navigate({ pub_service: `has_any:${normalize(svc)}` })}
                      onKeyDown={e =>
                        (e.key === 'Enter' || e.key === ' ') &&
                        navigate({ pub_service: `has_any:${normalize(svc)}` })
                      }
                    >
                      {svc}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MegaMenu;
